import { Controller } from '@hotwired/stimulus';
import { loadStripe } from '@stripe/stripe-js';
import { Toast } from 'rad_common/toast';
import * as Sentry from '@sentry/browser';

export default class extends Controller {
  static values = {
    publishableKey: String,
    clientSecret: String,
    clientName: String,
    clientEmail: String
  };

  static targets = ['form', 'paymentElement', 'submitButton', 'paymentContainer'];

  async connect() {
    this.stripe = await loadStripe(this.publishableKeyValue);

    if (this.hasFormTarget) {
      await this.initializePayment();
    }
  }

  async initializePayment() {
    try {
      this.clientSecret = this.paymentContainerTarget.dataset.stripePaymentIntentClientSecret;

      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#5469d4',
          }
        }
      });

      const paymentElement = this.elements.create('payment', {
        paymentMethodOrder: ['us_bank_account', 'card'],
        layout: {
          type: 'accordion',
          visibleAccordionItemsCount: 1
        }
      });
      paymentElement.mount('#payment-element');

      paymentElement.on('change', (event) => {
        if (event.error) {
          this.showError(event.error.message);
        }
      });

    } catch (error) {
      console.error('Error initializing payment:', error);
      this.captureSentry(error, 'initializePayment', { clientSecret: this.clientSecret });
      this.showError('Failed to initialize payment form. Please refresh and try again.');
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.setLoading(true);

    let elements = this.elements;
    let clientSecret = this.clientSecret;

    const { error: submitError } = await elements.submit();

    if (submitError) {
      this.showError(submitError.message);
      this.setLoading(false);
      return;
    }

    try {
      const { error } = await this.stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/payment_success`,
          payment_method_data: {
            billing_details: {
              name: this.clientNameValue,
              email: this.clientEmailValue
            }
          }
        },
        redirect: 'always'
      });

      // This code only executes if redirect fails (very rare)
      if (error) {
        this.captureSentry(
          new Error(`Stripe payment error: ${error.message}`),
          'handleSubmit',
          { errorType: error.type, errorCode: error.code }
        );
        this.showError(error.message);
        this.setLoading(false);
      }

    } catch (error) {
      console.error('Error during payment:', error);
      this.captureSentry(error, 'handleSubmit');
      this.showError('An unexpected error occurred. Please try again.');
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isLoading;
    }
  }

  showError(message) {
    Toast.error('Error', message);
  }

  captureSentry(error, action, extraContext = {}) {
    const baseContext = {
      tags: { component: 'stripe_payment_form', action },
      extra: {
        clientName: this.clientNameValue,
        clientEmail: this.clientEmailValue,
        ...extraContext
      }
    };

    Sentry.captureException(error, baseContext);
  }
}
